import './CreateReference.sass';

import React from 'react';
import { Col, Row } from 'react-bootstrap';
import Button from 'react-bootstrap-button-loader';

import {
  CHAPTER_AUDIO_PLAYLIST_GENERATION_ERROR,
  CHAPTER_AUDIO_PLAYLIST_GENERATION_LOADING,
  CHAPTER_AUDIO_PLAYLIST_GENERATION_PENDING,
  CHAPTER_AUDIO_PLAYLIST_GENERATION_SUCCESS,
} from '../../../components/Books/constants';
import {
  dateOptions,
  localeDateName,
  timeOptions,
} from '../../../constants/date';
import AudioVoiceSelector from '../../AudioVoiceSelector/AudioVoiceSelector';
import DropFileInput from '../../DropFileInput/DropFileInput';
import { SimpleInput, SimpleTextarea } from '../../Input/Input';
import Spinner from '../../Spinner/Spinner';

class CreateReference extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      errors: null,
      waiting: false,
      name: this.props.reference.name,
      description: this.props.reference.description,
      adminNotes: this.props.reference.adminNotes,
      actAsParent: this.props.reference.actAsParent,
      pdf: this.props.reference.actAsParent
        ? this.props.reference.pdfUrl
        : this.props.reference.chapters[0].pdfUrl,
      enabled: this.props.reference.enabled,
      highlightBook: this.props.reference.highlightBook ?? false,
      weNeedThisPublication:
        this.props.reference.weNeedThisPublication ?? false,
      isRestricted: this.props.reference.isRestricted ?? false,

      // set hasPlayList and playlist only if is editing and reference NOT act as parent
      hasPlayList:
        this.props.edit && !this.props.reference.actAsParent
          ? !!this.props.reference.chapters[0].playlist
          : null,
      playlist:
        this.props.edit && !this.props.reference.actAsParent
          ? {
              ...this.props.reference.chapters[0].playlist,
              chapter: this.props.reference.chapters[0].id,
              voiceSelected: null,
              new: false, // this mark is for generate or re-generate audio
              upgrade: false, // this mark is for generate audios only for update or new questions
              delete: false, // this mark is for remove all audios including playlist
            }
          : null,
      // playlist for all chapters
      playlistForAll:
        this.props.edit && this.props.reference.actAsParent
          ? {
              new: false,
              voiceSelected: null,
            }
          : null,
      audioSummary:
        this.props.edit && !this.props.reference.actAsParent
          ? this.props.reference.chapters[0]?.audioSummary?.audioUrl
          : null,
      audioSummaryGenerationState:
        this.props.edit && !this.props.reference.actAsParent
          ? this.props.reference.chapters[0]?.audioSummary?.generationState
          : null,
    };
  }

  UNSAFE_componentWillReceiveProps({ submitSucceeded }) {
    if (submitSucceeded && this.state.waiting) {
      this.setState({ waiting: false });
    }
  }

  validate = () => {
    const errors = {};
    const fields = { ...this.state };

    if (!fields.name) {
      errors.name = 'Title field is required';
    }

    if (!fields.description) {
      errors.description = 'Description field is required';
    }

    if (fields.pdf?.name && fields.pdf.name.toLowerCase().endsWith('.pdf3')) {
      errors.pdf = 'Please select a pdf file';
    }

    if (
      fields.audioSummary?.name &&
      !fields.audioSummary.name.toLowerCase().endsWith('.mp3')
    ) {
      errors.audioSummary = 'Please select an MP3 audio file';
    }

    if (!fields.actAsParent) {
      fields.actAsParent = false;
    }

    if (fields.playlist?.audios) {
      // avoid send audio array to backend
      delete fields.playlist.audios;
    }

    if (fields.playlist?.new) {
      if (!fields.playlist.voiceSelected) {
        errors.voiceSelected = 'Please select a voice for audios';
      }
    }

    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    this.setState({ errors: null, waiting: true }, () => {
      this.props.onSubmit(fields);
    });
  };

  disableEditActsAsParent = () => {
    const { edit, reference } = this.props;

    if (!edit || !reference || !reference.id) {
      return false;
    }
    return reference.chapters.length > 0 && reference.actAsParent;
  };

  handleKeyPress = (event) => {
    if (event.ctrlKey && event.key === 'Enter') {
      document.getElementById('btn-success').click();
    }
  };

  handleGenerateAudioForAll = () => {
    const { playlistForAll } = this.state;
    this.setState({
      playlistForAll: {
        ...playlistForAll,
        new: !playlistForAll?.new,
      },
    });
  };

  handleGenerateAudio = () => {
    const { playlist } = this.state;
    this.setState({
      playlist: {
        ...playlist,
        new: !playlist?.new,
        upgrade: false,
        voiceSelected: null,
        delete: false,
      },
    });
  };

  handleUpgradeAudio = () => {
    const { playlist } = this.state;
    this.setState({
      playlist: {
        ...playlist,
        upgrade: !playlist?.upgrade,
        new: false,
        voiceSelected: null,
        delete: false,
      },
    });
  };

  handleRemoveAudio = () => {
    const { playlist } = this.state;
    this.setState({
      playlist: {
        ...playlist,
        delete: !playlist?.delete,
        upgrade: false,
        new: false,
        voiceSelected: null,
      },
    });
  };

  handleOnChangePlaylistEnabled = () => {
    const { playlist } = this.state;
    this.setState({
      playlist: {
        ...playlist,
        enabled: !playlist?.enabled,
        voiceSelected: null,
      },
    });
  };

  handleSelectVoice = (voice) => {
    const { playlist } = this.state;
    this.setState({
      playlist: {
        ...playlist,
        voiceSelected: voice,
      },
    });
  };

  handleSelectVoiceForAll = (voice) => {
    const { playlistForAll } = this.state;
    this.setState({
      playlistForAll: {
        ...playlistForAll,
        voiceSelected: voice,
      },
    });
  };

  getPlayListCreatedDate = () => {
    const date = new Date(this.state.playlist?.updatedAt);
    const formattedDate = date.toLocaleDateString(localeDateName, dateOptions);
    const formattedTime = date.toLocaleTimeString(localeDateName, timeOptions);

    const formattedDateTime = this.state.playlist?.updatedAt
      ? `${formattedDate} ${formattedTime}`
      : '-';
    return formattedDateTime;
  };

  render() {
    const { onCancel, edit = false, reference = {}, onDelete } = this.props;
    const title = edit ? 'Edit Book' : 'Add Book';
    const isSomePlaylistLoading =
      edit &&
      this.props.reference.chapters.some(
        (chapter) =>
          chapter.playlist?.generationState ===
          CHAPTER_AUDIO_PLAYLIST_GENERATION_LOADING,
      );

    return (
      <div className="create-chapter-component">
        <div className="card-block">
          <div className="createChapterTitle">
            {title}
            <i
              className="fa fa-times fa-xs"
              onClick={() => this.props.onCancel()}
            />
            <hr style={{ width: '600px', marginLeft: '-15px' }} />
          </div>
          <form
            onKeyDown={this.handleKeyPress}
            className="widget-form new-admin-form"
            onSubmit={this.validate}
          >
            <Row>
              <Col md={5}>Title</Col>
              <Col md={7}>
                <SimpleInput
                  type="text"
                  value={this.state.name}
                  onChange={(e) => {
                    this.setState({ name: e.target.value });
                  }}
                  error={this.state.errors?.name ?? null}
                  dataTest="create-book-title-input"
                />
              </Col>
            </Row>
            <Row>
              <Col md={5}>Description</Col>
              <Col md={7}>
                <SimpleTextarea
                  type="text"
                  value={this.state.description}
                  onChange={(e) => {
                    this.setState({ description: e.target.value });
                  }}
                  error={this.state.errors?.description ?? null}
                  dataTest="create-book-description-input"
                />
              </Col>
            </Row>
            <Row>
              <Col md={5}>Admin notes</Col>
              <Col md={7}>
                <SimpleTextarea
                  type="text"
                  value={this.state.adminNotes}
                  onChange={(e) => {
                    this.setState({ adminNotes: e.target.value });
                  }}
                  error={this.state.errors?.adminNotes ?? null}
                  dataTest="create-book-admin-notes-input"
                />
              </Col>
            </Row>
            <Row>
              <Col md={5}>
                <span className="InputLebel">Act as a parent</span>
              </Col>
              <Col md={7} className="mt-10">
                <SimpleInput
                  type="checkbox"
                  value={this.state.actAsParent}
                  onChange={() => {
                    this.setState({ actAsParent: !this.state.actAsParent });
                  }}
                  disabled={this.disableEditActsAsParent()}
                  dataTest="create-book-act-as-parent-input"
                />
              </Col>
            </Row>
            <Row>
              <Col md={5}>
                <span className="InputLebel">Enabled</span>
              </Col>
              <Col md={7} className="mt-10">
                <SimpleInput
                  type="checkbox"
                  value={this.state.enabled}
                  onChange={() => {
                    this.setState({ enabled: !this.state.enabled });
                  }}
                  dataTest="create-book-enabled-input"
                />
              </Col>
            </Row>
            <Row>
              <Col md={5} className="mt-10">
                <span className="InputLebel">Highlight book</span>
              </Col>
              <Col md={7}>
                <SimpleInput
                  type="checkbox"
                  value={this.state.highlightBook}
                  onChange={() => {
                    this.setState({ highlightBook: !this.state.highlightBook });
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={5}>
                <span className="InputLebel">We need this publication!</span>
              </Col>
              <Col md={7} className="mt-10">
                <SimpleInput
                  type="checkbox"
                  value={this.state.weNeedThisPublication}
                  onChange={() => {
                    this.setState({
                      weNeedThisPublication: !this.state.weNeedThisPublication,
                    });
                  }}
                  data-test="create-book-need-publication-input"
                />
              </Col>
            </Row>
            <Row>
              <Col md={5}>
                <label className="InputLebel" htmlFor="isRestricted">
                  Is restricted
                </label>
              </Col>
              <Col md={7} className="mt-10">
                <SimpleInput
                  type="checkbox"
                  value={this.state.isRestricted}
                  onChange={() => {
                    this.setState({
                      isRestricted: !this.state.isRestricted,
                    });
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col md={5}>
                <span className="InputLebel">Pdf</span>
              </Col>
              <Col md={7} className="mt-10">
                <DropFileInput
                  input={{
                    onChange: (e) => {
                      this.setState({ pdf: e });
                    },
                  }}
                  value={this.state.pdf}
                  defaultValue={this.state.pdf}
                  multiple={false}
                  accept={{ 'application/pdf': ['.pdf'] }}
                  getUrlPdf={this.props.getUrlPdf}
                />
              </Col>
            </Row>

            {this.state.actAsParent && (
              <>
                {edit && isSomePlaylistLoading ? (
                  <Spinner text="Generating or upgrading audio" />
                ) : (
                  <Row>
                    <Col md={5}>
                      <label
                        className="InputLebel"
                        htmlFor="generatePlaylistAll"
                      >
                        Generate playlist for all chapters
                      </label>
                    </Col>
                    <Col
                      md={this.state.playlistForAll?.new ? 1 : 7}
                      className="mt-10"
                    >
                      <SimpleInput
                        type="checkbox"
                        value={this.state.playlistForAll?.new}
                        checked={this.state.playlistForAll?.new}
                        onChange={this.handleGenerateAudioForAll}
                      />
                    </Col>
                    {this.state.playlistForAll?.new && (
                      <Col md={6} className="mt-10">
                        <AudioVoiceSelector
                          onSelectVoice={this.handleSelectVoiceForAll}
                        />
                      </Col>
                    )}
                  </Row>
                )}
              </>
            )}
            {!this.state.actAsParent && (
              <>
                <Row>
                  {edit &&
                    this.state.hasPlayList &&
                    this.state.playlist?.generationState ===
                      CHAPTER_AUDIO_PLAYLIST_GENERATION_SUCCESS && (
                      <>
                        <Col md={4}>
                          <span>Audio</span>
                        </Col>
                        <Col md={8} className="mt-10">
                          <Row className="playlistInfo">
                            <table border="1" cellPadding="8" cellSpacing="0">
                              <tbody>
                                <tr>
                                  <td>Created or updated at</td>
                                  <td>{this.getPlayListCreatedDate()}</td>
                                </tr>
                                <tr>
                                  <td>Amount of questions</td>
                                  <td>
                                    {this.state.playlist?.audiosCount ?? 0}
                                  </td>
                                </tr>
                                <tr>
                                  <td>Generated with voice</td>
                                  <td>
                                    {this.state.playlist?.voice?.name ??
                                      'no-data'}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </Row>
                          <Row className="generateVoiceOptions">
                            <label>
                              <SimpleInput
                                type="checkbox"
                                checked={!this.state.playlist?.enabled}
                                onChange={this.handleOnChangePlaylistEnabled}
                              />
                              Disabled
                            </label>

                            {this.state.playlist?.enabled && (
                              <>
                                <label>
                                  <SimpleInput
                                    type="checkbox"
                                    checked={this.state.playlist?.upgrade}
                                    onChange={this.handleUpgradeAudio}
                                  />
                                  Upgrade
                                </label>

                                <label>
                                  <SimpleInput
                                    type="checkbox"
                                    checked={this.state.playlist?.new}
                                    onChange={this.handleGenerateAudio}
                                  />
                                  Re-generate
                                </label>

                                <label>
                                  <SimpleInput
                                    type="checkbox"
                                    checked={this.state.playlist?.delete}
                                    onChange={this.handleRemoveAudio}
                                  />
                                  Remove Playlist (all audios)
                                </label>

                                {this.state.playlist?.new && (
                                  <AudioVoiceSelector
                                    onSelectVoice={this.handleSelectVoice}
                                  />
                                )}
                              </>
                            )}
                          </Row>
                        </Col>
                      </>
                    )}
                  {edit &&
                    this.state.hasPlayList &&
                    this.state.playlist?.generationState ===
                      CHAPTER_AUDIO_PLAYLIST_GENERATION_LOADING && (
                      <Spinner text="Generating or upgrading audio" />
                    )}
                  {edit &&
                    (!this.state.hasPlayList ||
                      this.state.playlist?.generationState ===
                        CHAPTER_AUDIO_PLAYLIST_GENERATION_PENDING ||
                      this.state.playlist?.generationState ===
                        CHAPTER_AUDIO_PLAYLIST_GENERATION_ERROR) && (
                      <>
                        <Col md={5}>
                          <span>Generate Audio Playlist</span>
                        </Col>
                        <Col md={7} className="d-flex align-items-start">
                          <Row className="generateVoiceOptions">
                            <SimpleInput
                              type="checkbox"
                              checked={this.state.playlist?.new}
                              onChange={this.handleGenerateAudio}
                            />
                            {this.state.playlist?.new && (
                              <AudioVoiceSelector
                                onSelectVoice={this.handleSelectVoice}
                              />
                            )}
                          </Row>
                        </Col>
                      </>
                    )}
                </Row>
                <Row>
                  <Col md={5}>
                    <span className="InputLebel">Audio Summary</span>
                  </Col>
                  <Col md={5} className="mt-10">
                    {this.state.audioSummaryGenerationState ===
                    CHAPTER_AUDIO_PLAYLIST_GENERATION_LOADING ? (
                      <Spinner text="Generating audio summary" />
                    ) : (
                      <DropFileInput
                        input={{
                          onChange: (e) => {
                            this.setState({ audioSummary: e });
                          },
                        }}
                        value={this.state.audioSummary}
                        defaultValue={this.state.audioSummary}
                        multiple={false}
                        accept="audio/mp3,audio/mpeg"
                        getUrlPdf={this.props.getUrlPdf}
                      />
                    )}
                  </Col>
                  <Col md={2}>
                    {this.state.errors?.audioSummary && (
                      <span className="errors">
                        {this.state.errors.audioSummary}
                      </span>
                    )}
                  </Col>
                </Row>
              </>
            )}

            {this.state.errors?.general && (
              <div className="errors">{this.state.errors.general}</div>
            )}

            <hr style={{ width: '600px', marginLeft: '-15px' }} />
            <div className="bottom-buttons-new">
              {edit && (
                <i
                  className="fa fa-trash icon-button"
                  title="Remove this reference."
                  data-test="delete-book-button"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onDelete(reference);
                  }}
                />
              )}
              <Button
                id="btn-success"
                className="btn btn-success"
                onClick={this.validate}
                loading={this.state.waiting}
              >
                Submit
              </Button>
              <Button
                data-test="cancel-edit-button"
                className="btn btn-secondary"
                onClick={onCancel}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

CreateReference.defaultProps = {
  reference: {
    name: '',
    description: '',
    adminNotes: '',
    actAsParent: true,
    pdf: '',
    enabled: true,
    weNeedThisPublication: false,
    isRestricted: false,
  },
};

export default CreateReference;
